import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import dayjs from 'dayjs';

import AsyncTable, { Column } from '../../../../../components/AsyncTable';
import getGenericRequestPageForAsyncTable from '../../../../../utils/TablesFunctions';
import { DatasyncLog } from '../../../../../types/apiResponse/datasync';
import { datasyncLogsAPI } from '../../../../../services/requests';
import ViewLog from '../../../../../components/ViewLog';
import { PreviewIcon, CopyIcon } from '../../../../../icons';
import { ActionButtonsContainer } from '../../../../../components/TablePaginator/styles';
import { frontendNotification } from '../../../../../components/Notification';
import BasicDateTimePicker from '../../../../../components/SelectDateTime';
import Btn from '../../../../../components/Buttons/Btn';
import SimpleSelect from '../../../../../components/SimpleSelect';

const DatasyncLogsPage: React.FC = () => {
  const [tableData, setTableData] = useState<DatasyncLog[]>([]);
  const [viewLog, setViewLog] = useState(false);
  const [logSelected, setLogSelected] = useState<DatasyncLog | null>(null);
  const [startDate, setStartDate] = useState<any>(null);
  const [endDate, setEndDate] = useState<any>(null);
  const [logType, setLogType] = useState<string>('');
  const [appliedStartDate, setAppliedStartDate] = useState<any>(null);
  const [appliedEndDate, setAppliedEndDate] = useState<any>(null);
  const [appliedLogType, setAppliedLogType] = useState<string>('');
  const theme = useTheme();
  const { t } = useTranslation();

  const handleTableViewLogBtn = (row: DatasyncLog) => {
    setLogSelected(row);
    setViewLog(true);
  };

  const handleTableCopyBtn = (row: DatasyncLog) => {
    navigator.clipboard.writeText(JSON.stringify(row, null, 2));
    frontendNotification({
      message: t('copiedSuccessfully'),
      type: 'success',
    });
  };

  const actionsButtons = (row: DatasyncLog) => (
    <ActionButtonsContainer type={4}>
      <PreviewIcon title={t('view')} onClick={() => handleTableViewLogBtn(row)} />
      <CopyIcon title={t('copy')} onClick={() => handleTableCopyBtn(row)} />
    </ActionButtonsContainer>
  );

  const getTypeColor = (type: string) => {
    switch (type?.toLowerCase()) {
      case 'warning':
        return theme.colors.warning;
      case 'error':
        return theme.colors.negative;
      case 'success':
        return theme.colors.positive;
      default:
        return theme.colors.text;
    }
  };

  const columns: Column<DatasyncLog>[] = [
    {
      accessor: 'type',
      label: t('logType'),
      Cell: (row: DatasyncLog) => (
        <span style={{ color: getTypeColor(row.type) }}>{row.type}</span>
      ),
      headerCellStyle: { minWidth: '6rem' }
    },
    { accessor: 'id', label: t('id') },
    { accessor: 'datasync_type', label: t('type') },
    { accessor: 'message', label: t('message') },
    {
      accessor: 'data',
      label: t('data'),
      Cell: (row: DatasyncLog) => {
        const content = row.data ? JSON.stringify(row.data) : '-';
        const maxLength = 100;
        return content.length > maxLength ? content.substring(0, maxLength) + '...' : content;
      }
    },
    {
      accessor: 'duration',
      label: t('duration'),
      Cell: (row: DatasyncLog) => row.duration || '-',
      headerCellStyle: { minWidth: '6rem' }
    },
    {
      accessor: 'created_at',
      label: t('createdAt'),
      headerCellStyle: { minWidth: '6rem' }
    },
    {
      Cell: actionsButtons,
    },
  ];

  const query = useMemo(() => {
    let q = '';
    // eslint-disable-next-line max-len
    if (appliedStartDate) q += `&start_date=${dayjs(appliedStartDate).format('YYYY-MM-DD HH:mm:ss')}`;
    if (appliedEndDate) q += `&end_date=${dayjs(appliedEndDate).format('YYYY-MM-DD HH:mm:ss')}`;
    if (appliedLogType) q += `&type=${appliedLogType}`;
    return q;
  }, [appliedStartDate, appliedEndDate, appliedLogType]);

  const handleFilter = () => {
    setAppliedStartDate(startDate);
    setAppliedEndDate(endDate);
    setAppliedLogType(logType);
  };

  const logTypeOptions = [
    { value: '', label: t('allTypes') },
    { value: 'error', label: t('error') },
    { value: 'warning', label: t('warning') },
    { value: 'success', label: t('success') },
  ];

  return (
    <div>
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', alignItems: 'center' }}>
        <BasicDateTimePicker
          value={startDate}
          onChange={setStartDate}
          disablePast={false}
          label={t('startDate') as string}
        />
        <BasicDateTimePicker
          value={endDate}
          onChange={setEndDate}
          disablePast={false}
          label={t('endDate') as string}
        />
        <div style={{ width: '200px' }}>
          <SimpleSelect
            options={logTypeOptions}
            value={logType}
            onChange={(e: any) => setLogType(e.target.value)}
            placeholder={t('selectType')}
            label={t('type') as string}
          />
        </div>
        <Btn
          text={t('filter') as string}
          onClick={handleFilter}
          style={{ height: '56px', margin: 0 }}
        />
      </div>
      <AsyncTable
        tableName="datasync-logs"
        columns={columns}
        value={tableData}
        onChange={setTableData}
        requestPage={getGenericRequestPageForAsyncTable(datasyncLogsAPI, query)}
        reqListener={[appliedStartDate, appliedEndDate, appliedLogType]}
        options={{
          styles: {
            primaryColor: `${theme.colors.surface}`,
            secondaryColor: `${theme.colors.onSurface}`,
            alternateRowColor: theme.colors.onSurfaceLight,
            textColor: theme.colors.text,
          },
          quantityPerPageLabel: t('quantityPerPage'),
          searchPlaceholder: t('search'),
          totalPageLabel: `${t('totalPages')}: `,
        }}
      />

      {viewLog && (
        <ViewLog
          showModal={viewLog}
          closeModal={() => setViewLog(false)}
          log={JSON.stringify(logSelected, null, 2)}
        />
      )}
    </div>
  );
};

export default DatasyncLogsPage;
